import { produce } from 'immer';
import type {
  VoiceAgentSummary,
  TopItemRow,
  TimeSavedResponse,
  VoiceOrderRow,
  ConversionFunnelResponse,
  CallActivityLogRow,
  CallTranscriptResponse,
  CallVolumeByHourItem,
  CallVolumeByDayItem,
  OrdersByDayItem,
  DayBucketGranularity,
  OutcomeDistributionItem,
  RevenueKpiResponse,
  TopRevenueItemRow,
  TransfersResponse,
  TransferReasonsResponse,
} from '../../../services/voiceAgentApi';
import {
  FETCH_VOICE_AGENT_SUMMARY_REQUEST,
  FETCH_VOICE_AGENT_SUMMARY_SUCCESS,
  FETCH_VOICE_AGENT_SUMMARY_FAILURE,
  FETCH_VOICE_AGENT_TOP_ITEMS_REQUEST,
  FETCH_VOICE_AGENT_TOP_ITEMS_SUCCESS,
  FETCH_VOICE_AGENT_TOP_ITEMS_FAILURE,
  FETCH_VOICE_AGENT_TIME_SAVED_REQUEST,
  FETCH_VOICE_AGENT_TIME_SAVED_SUCCESS,
  FETCH_VOICE_AGENT_TIME_SAVED_FAILURE,
  FETCH_VOICE_AGENT_ORDERS_BY_DAY_REQUEST,
  FETCH_VOICE_AGENT_ORDERS_BY_DAY_SUCCESS,
  FETCH_VOICE_AGENT_ORDERS_BY_DAY_FAILURE,
  FETCH_VOICE_AGENT_ORDERS_REQUEST,
  FETCH_VOICE_AGENT_ORDERS_SUCCESS,
  FETCH_VOICE_AGENT_ORDERS_FAILURE,
  FETCH_VOICE_AGENT_CONVERSION_FUNNEL_REQUEST,
  FETCH_VOICE_AGENT_CONVERSION_FUNNEL_SUCCESS,
  FETCH_VOICE_AGENT_CONVERSION_FUNNEL_FAILURE,
  FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_REQUEST,
  FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_SUCCESS,
  FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_FAILURE,
  FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_OUTCOMES_REQUEST,
  FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_OUTCOMES_SUCCESS,
  FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_OUTCOMES_FAILURE,
  SET_CALL_ACTIVITY_LOG_OUTCOME_FILTER,
  FETCH_VOICE_AGENT_CALL_TRANSCRIPT_REQUEST,
  FETCH_VOICE_AGENT_CALL_TRANSCRIPT_SUCCESS,
  FETCH_VOICE_AGENT_CALL_TRANSCRIPT_FAILURE,
  CLEAR_VOICE_AGENT_CALL_TRANSCRIPT,
  FETCH_VOICE_AGENT_CALL_VOLUME_BY_HOUR_REQUEST,
  FETCH_VOICE_AGENT_CALL_VOLUME_BY_HOUR_SUCCESS,
  FETCH_VOICE_AGENT_CALL_VOLUME_BY_HOUR_FAILURE,
  FETCH_VOICE_AGENT_CALL_VOLUME_BY_DAY_REQUEST,
  FETCH_VOICE_AGENT_CALL_VOLUME_BY_DAY_SUCCESS,
  FETCH_VOICE_AGENT_CALL_VOLUME_BY_DAY_FAILURE,
  FETCH_VOICE_AGENT_OUTCOME_DISTRIBUTION_REQUEST,
  FETCH_VOICE_AGENT_OUTCOME_DISTRIBUTION_SUCCESS,
  FETCH_VOICE_AGENT_OUTCOME_DISTRIBUTION_FAILURE,
  FETCH_VOICE_AGENT_REVENUE_KPI_REQUEST,
  FETCH_VOICE_AGENT_REVENUE_KPI_SUCCESS,
  FETCH_VOICE_AGENT_REVENUE_KPI_FAILURE,
  FETCH_VOICE_AGENT_TOP_REVENUE_ITEMS_REQUEST,
  FETCH_VOICE_AGENT_TOP_REVENUE_ITEMS_SUCCESS,
  FETCH_VOICE_AGENT_TOP_REVENUE_ITEMS_FAILURE,
  FETCH_VOICE_AGENT_TRANSFERS_REQUEST,
  FETCH_VOICE_AGENT_TRANSFERS_SUCCESS,
  FETCH_VOICE_AGENT_TRANSFERS_FAILURE,
  FETCH_VOICE_AGENT_TRANSFER_REASONS_REQUEST,
  FETCH_VOICE_AGENT_TRANSFER_REASONS_SUCCESS,
  FETCH_VOICE_AGENT_TRANSFER_REASONS_FAILURE,
} from './voiceAgentConstants';

const initialState: {
  // Summary — 5 live stamp KPIs
  summaryLoading: boolean;
  summary: VoiceAgentSummary | null;
  summaryFailure: boolean;
  // Top Items Ordered via Voice
  topItemsLoading: boolean;
  topItems: TopItemRow[];
  topItemsFailure: boolean;
  // Estimated Time Saved
  timeSavedLoading: boolean;
  timeSaved: TimeSavedResponse | null;
  timeSavedFailure: boolean;
  // Orders By Day
  ordersByDayLoading: boolean;
  ordersByDay: OrdersByDayItem[];
  ordersByDayGranularity: DayBucketGranularity;
  ordersByDayFailure: boolean;
  // Recent Voice Orders
  ordersLoading: boolean;
  orders: VoiceOrderRow[];
  ordersTotalElements: number;
  ordersTotalPages: number;
  ordersFailure: boolean;
  // Conversion Funnel
  conversionFunnelLoading: boolean;
  conversionFunnel: ConversionFunnelResponse | null;
  conversionFunnelFailure: boolean;
  // Call Activity Log
  callActivityLogLoading: boolean;
  callActivityLog: CallActivityLogRow[];
  callActivityLogTotalElements: number;
  callActivityLogTotalPages: number;
  callActivityLogFailure: boolean;
  // Call Activity Log outcome filter options
  callActivityLogOutcomes: string[];
  callActivityLogOutcomesFailure: boolean;
  // Call Activity Log — selected outcome filter (persisted; see voiceAgentIndex.ts)
  callActivityLogOutcomeFilter: string;
  // Call Transcript
  callTranscriptLoading: boolean;
  callTranscript: CallTranscriptResponse | null;
  callTranscriptFailure: boolean;
  callTranscriptError: string | null;
  // Call Volume by Hour
  callVolumeByHourLoading: boolean;
  callVolumeByHour: CallVolumeByHourItem[];
  callVolumeByHourFailure: boolean;
  // Call Volume by Day
  callVolumeByDayLoading: boolean;
  callVolumeByDay: CallVolumeByDayItem[];
  callVolumeByDayGranularity: DayBucketGranularity;
  callVolumeByDayFailure: boolean;
  // Outcome Distribution
  outcomeDistributionLoading: boolean;
  outcomeDistribution: OutcomeDistributionItem[];
  outcomeDistributionFailure: boolean;
  // Voice Revenue KPI
  revenueKpiLoading: boolean;
  revenueKpi: RevenueKpiResponse | null;
  revenueKpiFailure: boolean;
  // Top Revenue Items
  topRevenueItemsLoading: boolean;
  topRevenueItems: TopRevenueItemRow[];
  topRevenueItemsFailure: boolean;
  // Transfers
  transfersLoading: boolean;
  transfers: TransfersResponse | null;
  transfersFailure: boolean;
  // Transfer Reasons
  transferReasonsLoading: boolean;
  transferReasons: TransferReasonsResponse | null;
  transferReasonsFailure: boolean;
} = {
  // Loading defaults to true (not false) for every fetch that a screen/drill
  // renders as soon as it mounts — mirrors the pre-Redux hooks' `useState(true)`.
  // The dispatching component is responsible for reading it as `false` when it
  // knows up front there's nothing to fetch (e.g. no locationId yet), the same
  // way the original hooks explicitly set `loading=false` in that case rather
  // than firing a request. Skipping this makes the very first render — before
  // the mount effect's dispatch has had a chance to flip it back on — briefly
  // read as "not loading" with no data, flashing an empty/"no results" state
  // ahead of the real shimmer.
  summaryLoading: true,
  summary: null,
  summaryFailure: false,

  topItemsLoading: true,
  topItems: [],
  topItemsFailure: false,

  timeSavedLoading: true,
  timeSaved: null,
  timeSavedFailure: false,

  ordersByDayLoading: true,
  ordersByDay: [],
  ordersByDayGranularity: 'day',
  ordersByDayFailure: false,

  ordersLoading: true,
  orders: [],
  ordersTotalElements: 0,
  ordersTotalPages: 1,
  ordersFailure: false,

  conversionFunnelLoading: true,
  conversionFunnel: null,
  conversionFunnelFailure: false,

  callActivityLogLoading: true,
  callActivityLog: [],
  callActivityLogTotalElements: 0,
  callActivityLogTotalPages: 1,
  callActivityLogFailure: false,

  callActivityLogOutcomes: [],
  callActivityLogOutcomesFailure: false,
  callActivityLogOutcomeFilter: 'all',

  callTranscriptLoading: false,
  callTranscript: null,
  callTranscriptFailure: false,
  callTranscriptError: null,

  callVolumeByHourLoading: true,
  callVolumeByHour: [],
  callVolumeByHourFailure: false,

  callVolumeByDayLoading: true,
  callVolumeByDay: [],
  callVolumeByDayGranularity: 'day',
  callVolumeByDayFailure: false,

  outcomeDistributionLoading: true,
  outcomeDistribution: [],
  outcomeDistributionFailure: false,

  revenueKpiLoading: true,
  revenueKpi: null,
  revenueKpiFailure: false,

  topRevenueItemsLoading: true,
  topRevenueItems: [],
  topRevenueItemsFailure: false,

  transfersLoading: true,
  transfers: null,
  transfersFailure: false,

  transferReasonsLoading: true,
  transferReasons: null,
  transferReasonsFailure: false,
};

export default function voiceAgentReducer(state = initialState, action: any) {
  return produce(state, (draft) => {
    switch (action.type) {
      case FETCH_VOICE_AGENT_SUMMARY_REQUEST:
        draft.summaryLoading = true;
        draft.summary = null;
        draft.summaryFailure = false;
        break;
      case FETCH_VOICE_AGENT_SUMMARY_SUCCESS:
        draft.summaryLoading = false;
        draft.summary = action.payload;
        draft.summaryFailure = false;
        break;
      case FETCH_VOICE_AGENT_SUMMARY_FAILURE:
        draft.summaryLoading = false;
        draft.summary = null;
        draft.summaryFailure = true;
        break;

      case FETCH_VOICE_AGENT_TOP_ITEMS_REQUEST:
        draft.topItemsLoading = true;
        draft.topItems = [];
        draft.topItemsFailure = false;
        break;
      case FETCH_VOICE_AGENT_TOP_ITEMS_SUCCESS:
        draft.topItemsLoading = false;
        draft.topItems = action.payload?.data ?? [];
        draft.topItemsFailure = false;
        break;
      case FETCH_VOICE_AGENT_TOP_ITEMS_FAILURE:
        draft.topItemsLoading = false;
        draft.topItems = [];
        draft.topItemsFailure = true;
        break;

      case FETCH_VOICE_AGENT_TIME_SAVED_REQUEST:
        draft.timeSavedLoading = true;
        draft.timeSaved = null;
        draft.timeSavedFailure = false;
        break;
      case FETCH_VOICE_AGENT_TIME_SAVED_SUCCESS:
        draft.timeSavedLoading = false;
        draft.timeSaved = action.payload;
        draft.timeSavedFailure = false;
        break;
      case FETCH_VOICE_AGENT_TIME_SAVED_FAILURE:
        draft.timeSavedLoading = false;
        draft.timeSaved = null;
        draft.timeSavedFailure = true;
        break;

      case FETCH_VOICE_AGENT_ORDERS_BY_DAY_REQUEST:
        draft.ordersByDayLoading = true;
        draft.ordersByDay = [];
        draft.ordersByDayFailure = false;
        break;
      case FETCH_VOICE_AGENT_ORDERS_BY_DAY_SUCCESS:
        draft.ordersByDayLoading = false;
        draft.ordersByDay = action.payload?.days ?? [];
        draft.ordersByDayGranularity = action.payload?.granularity ?? 'day';
        draft.ordersByDayFailure = false;
        break;
      case FETCH_VOICE_AGENT_ORDERS_BY_DAY_FAILURE:
        draft.ordersByDayLoading = false;
        draft.ordersByDay = [];
        draft.ordersByDayFailure = true;
        break;

      case FETCH_VOICE_AGENT_ORDERS_REQUEST:
        draft.ordersLoading = true;
        draft.orders = [];
        draft.ordersFailure = false;
        break;
      case FETCH_VOICE_AGENT_ORDERS_SUCCESS:
        draft.ordersLoading = false;
        draft.orders = action.payload?.data ?? [];
        draft.ordersTotalElements = Number(action.payload?.pagination?.total) || 0;
        draft.ordersTotalPages = Number(action.payload?.pagination?.totalPages) || 1;
        draft.ordersFailure = false;
        break;
      case FETCH_VOICE_AGENT_ORDERS_FAILURE:
        draft.ordersLoading = false;
        draft.orders = [];
        draft.ordersTotalElements = 0;
        draft.ordersTotalPages = 1;
        draft.ordersFailure = true;
        break;

      case FETCH_VOICE_AGENT_CONVERSION_FUNNEL_REQUEST:
        draft.conversionFunnelLoading = true;
        draft.conversionFunnel = null;
        draft.conversionFunnelFailure = false;
        break;
      case FETCH_VOICE_AGENT_CONVERSION_FUNNEL_SUCCESS:
        draft.conversionFunnelLoading = false;
        draft.conversionFunnel = action.payload;
        draft.conversionFunnelFailure = false;
        break;
      case FETCH_VOICE_AGENT_CONVERSION_FUNNEL_FAILURE:
        draft.conversionFunnelLoading = false;
        draft.conversionFunnel = null;
        draft.conversionFunnelFailure = true;
        break;

      case FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_REQUEST:
        draft.callActivityLogLoading = true;
        draft.callActivityLog = [];
        draft.callActivityLogFailure = false;
        break;
      case FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_SUCCESS:
        draft.callActivityLogLoading = false;
        draft.callActivityLog = action.payload?.data ?? [];
        draft.callActivityLogTotalElements = Number(action.payload?.pagination?.total) || 0;
        draft.callActivityLogTotalPages = Number(action.payload?.pagination?.totalPages) || 1;
        draft.callActivityLogFailure = false;
        break;
      case FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_FAILURE:
        draft.callActivityLogLoading = false;
        draft.callActivityLog = [];
        draft.callActivityLogTotalElements = 0;
        draft.callActivityLogTotalPages = 1;
        draft.callActivityLogFailure = true;
        break;

      case FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_OUTCOMES_REQUEST:
        draft.callActivityLogOutcomesFailure = false;
        break;
      case FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_OUTCOMES_SUCCESS:
        draft.callActivityLogOutcomes = action.payload?.outcomes ?? [];
        draft.callActivityLogOutcomesFailure = false;
        break;
      case FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_OUTCOMES_FAILURE:
        draft.callActivityLogOutcomes = [];
        draft.callActivityLogOutcomesFailure = true;
        break;

      case SET_CALL_ACTIVITY_LOG_OUTCOME_FILTER:
        draft.callActivityLogOutcomeFilter = action.payload;
        break;

      case FETCH_VOICE_AGENT_CALL_TRANSCRIPT_REQUEST:
        draft.callTranscriptLoading = true;
        draft.callTranscript = null;
        draft.callTranscriptFailure = false;
        draft.callTranscriptError = null;
        break;
      case FETCH_VOICE_AGENT_CALL_TRANSCRIPT_SUCCESS:
        draft.callTranscriptLoading = false;
        draft.callTranscript = action.payload;
        draft.callTranscriptFailure = false;
        draft.callTranscriptError = null;
        break;
      case FETCH_VOICE_AGENT_CALL_TRANSCRIPT_FAILURE:
        draft.callTranscriptLoading = false;
        draft.callTranscript = null;
        draft.callTranscriptFailure = true;
        draft.callTranscriptError = action.payload?.error || 'Failed to load call transcript.';
        break;
      case CLEAR_VOICE_AGENT_CALL_TRANSCRIPT:
        draft.callTranscriptLoading = false;
        draft.callTranscript = null;
        draft.callTranscriptFailure = false;
        draft.callTranscriptError = null;
        break;

      case FETCH_VOICE_AGENT_CALL_VOLUME_BY_HOUR_REQUEST:
        draft.callVolumeByHourLoading = true;
        draft.callVolumeByHour = [];
        draft.callVolumeByHourFailure = false;
        break;
      case FETCH_VOICE_AGENT_CALL_VOLUME_BY_HOUR_SUCCESS:
        draft.callVolumeByHourLoading = false;
        draft.callVolumeByHour = action.payload?.hours ?? [];
        draft.callVolumeByHourFailure = false;
        break;
      case FETCH_VOICE_AGENT_CALL_VOLUME_BY_HOUR_FAILURE:
        draft.callVolumeByHourLoading = false;
        draft.callVolumeByHour = [];
        draft.callVolumeByHourFailure = true;
        break;

      case FETCH_VOICE_AGENT_CALL_VOLUME_BY_DAY_REQUEST:
        draft.callVolumeByDayLoading = true;
        draft.callVolumeByDay = [];
        draft.callVolumeByDayFailure = false;
        break;
      case FETCH_VOICE_AGENT_CALL_VOLUME_BY_DAY_SUCCESS:
        draft.callVolumeByDayLoading = false;
        draft.callVolumeByDay = action.payload?.days ?? [];
        draft.callVolumeByDayGranularity = action.payload?.granularity ?? 'day';
        draft.callVolumeByDayFailure = false;
        break;
      case FETCH_VOICE_AGENT_CALL_VOLUME_BY_DAY_FAILURE:
        draft.callVolumeByDayLoading = false;
        draft.callVolumeByDay = [];
        draft.callVolumeByDayFailure = true;
        break;

      case FETCH_VOICE_AGENT_OUTCOME_DISTRIBUTION_REQUEST:
        draft.outcomeDistributionLoading = true;
        draft.outcomeDistribution = [];
        draft.outcomeDistributionFailure = false;
        break;
      case FETCH_VOICE_AGENT_OUTCOME_DISTRIBUTION_SUCCESS:
        draft.outcomeDistributionLoading = false;
        draft.outcomeDistribution = action.payload?.outcomes ?? [];
        draft.outcomeDistributionFailure = false;
        break;
      case FETCH_VOICE_AGENT_OUTCOME_DISTRIBUTION_FAILURE:
        draft.outcomeDistributionLoading = false;
        draft.outcomeDistribution = [];
        draft.outcomeDistributionFailure = true;
        break;

      case FETCH_VOICE_AGENT_REVENUE_KPI_REQUEST:
        draft.revenueKpiLoading = true;
        draft.revenueKpi = null;
        draft.revenueKpiFailure = false;
        break;
      case FETCH_VOICE_AGENT_REVENUE_KPI_SUCCESS:
        draft.revenueKpiLoading = false;
        draft.revenueKpi = action.payload;
        draft.revenueKpiFailure = false;
        break;
      case FETCH_VOICE_AGENT_REVENUE_KPI_FAILURE:
        draft.revenueKpiLoading = false;
        draft.revenueKpi = null;
        draft.revenueKpiFailure = true;
        break;

      case FETCH_VOICE_AGENT_TOP_REVENUE_ITEMS_REQUEST:
        draft.topRevenueItemsLoading = true;
        draft.topRevenueItems = [];
        draft.topRevenueItemsFailure = false;
        break;
      case FETCH_VOICE_AGENT_TOP_REVENUE_ITEMS_SUCCESS:
        draft.topRevenueItemsLoading = false;
        draft.topRevenueItems = action.payload?.items ?? [];
        draft.topRevenueItemsFailure = false;
        break;
      case FETCH_VOICE_AGENT_TOP_REVENUE_ITEMS_FAILURE:
        draft.topRevenueItemsLoading = false;
        draft.topRevenueItems = [];
        draft.topRevenueItemsFailure = true;
        break;

      case FETCH_VOICE_AGENT_TRANSFERS_REQUEST:
        draft.transfersLoading = true;
        draft.transfers = null;
        draft.transfersFailure = false;
        break;
      case FETCH_VOICE_AGENT_TRANSFERS_SUCCESS:
        draft.transfersLoading = false;
        draft.transfers = action.payload;
        draft.transfersFailure = false;
        break;
      case FETCH_VOICE_AGENT_TRANSFERS_FAILURE:
        draft.transfersLoading = false;
        draft.transfers = null;
        draft.transfersFailure = true;
        break;

      case FETCH_VOICE_AGENT_TRANSFER_REASONS_REQUEST:
        draft.transferReasonsLoading = true;
        draft.transferReasons = null;
        draft.transferReasonsFailure = false;
        break;
      case FETCH_VOICE_AGENT_TRANSFER_REASONS_SUCCESS:
        draft.transferReasonsLoading = false;
        draft.transferReasons = action.payload;
        draft.transferReasonsFailure = false;
        break;
      case FETCH_VOICE_AGENT_TRANSFER_REASONS_FAILURE:
        draft.transferReasonsLoading = false;
        draft.transferReasons = null;
        draft.transferReasonsFailure = true;
        break;

      default:
        return state;
    }
  });
}
